import api from './api'

// Типы
export interface ScheduleTemplate {
  id: string
  day_of_week: number
  day_name: string
  start_time: string
  end_time: string
  max_clients: number
  is_active: boolean
}

export interface ScheduleSeriesRequest {
  days: number[]
  start_time: string
  end_time: string
  duration_minutes: number
  start_interval_minutes: number
  max_clients: number
  breaks: { start_time: string; end_time: string }[]
  conflict_strategy?: 'keep' | 'replace'
  expected_revision?: string
}

export interface ScheduleSeriesPreview {
  revision: string
  create_count: number
  skip_count: number
  conflict_count: number
  intervals: {
    day_of_week: number
    day_name: string
    start_time: string
    end_time: string
    max_clients: number
    action: 'create' | 'skip' | 'conflict'
    existing: { end_time: string; max_clients: number; is_active: boolean }[]
  }[]
}

export async function previewScheduleSeries(data: ScheduleSeriesRequest) {
  return (await api.post<ScheduleSeriesPreview>('/schedules/templates/series/preview', data)).data
}

export async function createScheduleSeries(data: ScheduleSeriesRequest) {
  return (await api.post<{ created: number; skipped: number; replaced: number }>('/schedules/templates/series', data)).data
}

export interface TimeSlot {
  id: string
  date: string
  start_time: string
  end_time: string
  status: 'available' | 'booked' | 'blocked' | 'completed' | 'cancelled'
  max_clients?: number
  reserved_spots?: number
  pair_proposals?: {id:string;names:string;deadline:string;reserved_spots:number}[]
  can_edit?: boolean
  bookings_count?: number
  bookings?: {
    id: string
    client_name: string
    client_photo_url: string | null
    pair_status?: 'accepted' | 'cancelled'
    pair_partner_name?: string
    guest_details?: string
  }[]
}

// API для шаблонов расписания
export async function getScheduleTemplates() {
  const response = await api.get<{ templates: ScheduleTemplate[] }>('/schedules/templates')
  return response.data.templates
}

export async function createScheduleTemplate(data: {
  day_of_week: number
  start_time: string
  end_time: string
  max_clients: number
}) {
  const response = await api.post<ScheduleTemplate>('/schedules/templates', data)
  return response.data
}

export async function updateScheduleTemplate(
  id: string,
  data: {
    start_time?: string
    end_time?: string
    max_clients?: number
    is_active?: boolean
  }
) {
  const response = await api.patch<ScheduleTemplate>(`/schedules/templates/${id}`, data)
  return response.data
}

export async function deleteScheduleTemplate(id: string) {
  await api.delete(`/schedules/templates/${id}`)
}

// API для слотов
export async function getTimeSlots(dateFrom?: string, dateTo?: string) {
  const params: Record<string, string> = {}
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo

  const response = await api.get<{
    slots: TimeSlot[]
    date_from: string
    date_to: string
  }>('/schedules/slots', { params })
  return response.data
}

export async function generateSlots(dateFrom: string, dateTo: string) {
  const response = await api.post<{
    message: string
    created: number
    date_from: string
    date_to: string
  }>('/schedules/slots/generate', {
    date_from: dateFrom,
    date_to: dateTo,
  })
  return response.data
}

export async function blockSlot(slotId: string) {
  const response = await api.post<{ message: string; status: string }>(
    `/schedules/slots/${slotId}/block`
  )
  return response.data
}

export async function unblockSlot(slotId: string) {
  const response = await api.post<{ message: string; status: string }>(
    `/schedules/slots/${slotId}/unblock`
  )
  return response.data
}

// Создание отдельного слота
export async function createSlot(data: {
  date: string
  start_time: string
  end_time: string
  max_clients?: number
}) {
  const response = await api.post<TimeSlot>('/schedules/slots', data)
  return response.data
}

// Удаление слота
export async function deleteSlot(slotId: string, force?: boolean) {
  await api.delete(`/schedules/slots/${slotId}`, {
    params: force ? { force: true } : undefined,
  })
}

// Массовое удаление слотов
export async function deleteSlotsBulk(data: {
  slot_ids?: string[]
  date_from?: string
  date_to?: string
  only_available?: boolean
}) {
  const response = await api.post<{ message: string; deleted: number }>(
    '/schedules/slots/delete-bulk',
    data
  )
  return response.data
}

export async function updateSlotCapacity(slotId: string, maxClients: number) {
  return (await api.patch(`/schedules/slots/${slotId}/capacity`, { max_clients: maxClients })).data
}
